// AppReauthSheet's twin: the sheet that asks for the password again before something sensitive, then the action carries
// on by itself (D74). The message says what needs it and for how long; the password field (autocomplete
// current-password, with a show toggle) keeps a wrong password in the sheet as its field error, announced. Continue
// stays enabled: pressed with nothing typed it asks for the password and calls nothing (D194). While `checking`, the
// confirm button spins and the sheet holds. The scrim never dismisses it: an action waits on the answer. `dialog` draws
// it as the centred dialog card windows from 600 px use (D218 (2)), named by its title and described by its message,
// where Escape is Cancel and the focus goes back to where it was; `danger` confirms
// with the danger button, for a destructive action. The words and the call are the caller's (features/reauth); ids end
// in `Sheet`, `Field` and `Button`.
import { type FormEvent, type ReactNode, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from './button'
import { Icon } from './icons'
import { DialogCard, DialogHead } from './modal'
import { Sheet } from './sheet'
import { TextField } from './text-field'

type ReauthSheetProps = {
  open: boolean
  title: ReactNode
  message: ReactNode
  passwordLabel: string
  value: string
  onValue: (value: string) => void
  error?: ReactNode
  checking?: boolean
  /** The centred dialog card instead of the bottom sheet (wide windows). */
  dialog?: boolean
  /** A destructive action waits: the confirm button is the danger one. */
  danger?: boolean
  confirmLabel: string
  cancelLabel: string
  onConfirm: () => void
  onCancel: () => void
  testId?: string
  fieldTestId?: string
  confirmTestId?: string
  cancelTestId?: string
}

export function ReauthSheet({
  open,
  title,
  message,
  passwordLabel,
  value,
  onValue,
  error,
  checking = false,
  dialog = false,
  danger = false,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  testId,
  fieldTestId,
  confirmTestId,
  cancelTestId,
}: ReauthSheetProps) {
  const { t } = useTranslation()
  const [shown, setShown] = useState(false)
  const [empty, setEmpty] = useState(false)
  const titleId = useId()
  const messageId = useId()
  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (checking) return
    if (value === '') setEmpty(true)
    else onConfirm()
  }
  const problem = error ?? (empty ? t('core.password.empty') : undefined)
  const form = (
    <form onSubmit={submit} noValidate className="flex flex-col gap-(--stack-md) pb-(--press-travel)">
      {!dialog && <p className="text-fg-muted">{message}</p>}
      <TextField
        label={passwordLabel}
        type={shown ? 'text' : 'password'}
        autoComplete="current-password"
        data-testid={fieldTestId}
        value={value}
        onChange={(event) => {
          setEmpty(false)
          onValue(event.target.value)
        }}
        errorText={
          problem ? (
            <span role="alert" data-problem={error ? 'refused' : 'empty'}>
              {problem}
            </span>
          ) : undefined
        }
        suffix={
          <button
            type="button"
            aria-label={t('core.password.show')}
            aria-pressed={shown}
            onClick={() => setShown(!shown)}
            className="-me-2 inline-flex size-(--touch-target-pointer) items-center justify-center rounded-full text-fg-muted hover:bg-hover focus-visible:outline-2 focus-visible:outline-focus"
          >
            <Icon name={shown ? 'eyeOff' : 'eye'} aria-hidden className="size-[18px]" />
          </button>
        }
      />
      <div className="flex flex-row-reverse flex-wrap gap-(--inline-md)">
        <Button type="submit" variant={danger ? 'danger' : 'primary'} loading={checking} data-testid={confirmTestId}>
          {confirmLabel}
        </Button>
        <Button type="button" variant="secondary" data-testid={cancelTestId} onClick={onCancel}>
          {cancelLabel}
        </Button>
      </div>
    </form>
  )
  if (!open) return null
  if (dialog) {
    return (
      <DialogCard
        dismissible={false}
        escapes
        onDismiss={onCancel}
        labelledBy={titleId}
        describedBy={messageId}
        testId={testId}
      >
        <DialogHead title={title} titleId={titleId} message={message} messageId={messageId} />
        <div className="px-(--inset-xl) pb-(--inset-xl)">{form}</div>
      </DialogCard>
    )
  }
  return (
    <Sheet open dismissible={false} onDismiss={onCancel} title={title} testId={testId}>
      {form}
    </Sheet>
  )
}
